import { Z } from "aldoo-ra/ZLayers"
import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
} from "react"
import { createPortal } from "react-dom"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline"

const VARIANT_ICONS = {
  warning: { Icon: ExclamationTriangleIcon, color: "text-warning" },
  error: { Icon: XCircleIcon, color: "text-error" },
  info: { Icon: InformationCircleIcon, color: "text-primary" },
  success: { Icon: CheckCircleIcon, color: "text-success" },
}

const MessageDialog = forwardRef((props, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])
  const [state, setState] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
    onClose: () => {},
    btnLabel: "",
    showCloseX: true,
    variant: "default",
  })

  const show = ({ title, message, onConfirm, onClose, btnLabel, showCloseX = true, variant = "default" }) => {
    setIsOpen(true)
    setState({
      title,
      message,
      onConfirm,
      onClose,
      btnLabel,
      showCloseX,
      variant,
    })
  }

  const { title, message, onConfirm, onClose, btnLabel, showCloseX, variant } = state
  const variantInfo = VARIANT_ICONS[variant]

  useImperativeHandle(ref, () => ({
    show,
  }))

  useEffect(() => {
    if (!isOpen && onClose) {
      onClose()
    }
  }, [isOpen])

  if (!isOpen) return null
  if (!mounted || typeof document === "undefined" || !document.body) return null

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
      style={{ zIndex: Z.system }}
    >
      <div className="bg-paper text-text rounded-lg shadow-lg w-[340px] sm:w-[400px] p-6 relative">
        {showCloseX && (
          <button
            className="absolute top-4 right-4 text-text_secondary hover:text-text transition-colors"
            onClick={() => {
              setIsOpen(false)
              if (onClose) onClose()
            }}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}

        <div className="flex items-center gap-2 mb-4 pr-8">
          {variantInfo?.Icon && (
            <variantInfo.Icon className={`w-6 h-6 shrink-0 ${variantInfo.color}`} />
          )}
          <Typography variant="admin_h3" className="text-text my-0">
            {title}
          </Typography>
        </div>

        <hr className="border-t border-bordered mb-4" />

        {typeof message === 'string' ? (
          <Typography className="mb-6 text-base text-text_secondary">
            {message}
          </Typography>
        ) : (
          <div className="mb-6 text-text">
            {message}
          </div>
        )}

        {btnLabel && (
          <div className="flex justify-center space-x-4 mt-5">
            <Button
              variant="primary"
              className="py-2 px-6 rounded-full"
              onClick={() => {
                setIsOpen(false)
                if (onConfirm) onConfirm()
              }}
            >
              {btnLabel}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
})

let messageInstance = null

export const showMessageDialog = (options) => {
  if (messageInstance === null) return
  messageInstance.show(options)
}

export const showMessageDialogAsync = (options) => {
  return new Promise((resolve) => {
    showMessageDialog({
      ...options,
      onConfirm: () => resolve(true),
    })
  })
}

const MessageDialogProvider = () => {
  const messageRef = useRef()

  useEffect(() => {
    messageInstance = messageRef.current
    return () => {
      messageInstance = null
    }
  }, [])

  return <MessageDialog ref={messageRef} />
}

export default MessageDialogProvider
